import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { Loading } from '@src/common/Loading';
import PropTypes from 'prop-types';
import { styled } from '@uhg-abyss/web/tools/styled';
import React from 'react';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { IconReview } from '@src/common/Icon/IconReview';
import { IconCheckedCircle } from '@src/common/Icon/IconCheckedCircle';
import { IconLaptop } from '@src/common/Icon/IconLaptop';
import { Link } from '@uhg-abyss/web/ui/Link';
import { IconInventory } from '@src/common/Icon/IconInventory';
import { config } from '@uhg-abyss/web/tools/config';
import { useTinRegistrationStatus } from '@src/state/tinRegistrationStatus';

const IconAlignment = styled('div', {
  display: 'inline-block',
  border: '1px solid green',
  backgroundColor: '$success1',
  borderRadius: '100%',
  width: '18%',
});

const Card = styled('div', {
  borderRadius: 3,
  boxSizing: 'border-box',
  border: '0.0625rem solid',
  borderTop: 'none',
  backgroundColor: '$gray1',
  borderColor: '$gray4',
  boxShadow: '0 0.125rem 0.3125rem 0 rgba(0,0,0,0.05)',
});

const TextStyleBold = styled('div', {
  fontWeight: 'bold',
});

const CompleteStatusText = styled('div', {
  color: '$success1',
});

const NotEligibleStatusText = styled('div', {
  color: '$error1',
});

const InProgressStatusText = styled('div', {
  color: '$info1',
});

const GrayText = styled('div', {
  color: '$gray6',
});

const MarginStyle = styled('div', {
  margin: '50px 10px 10px 0.825rem',
});

const FlexStages = styled('div', {
  textAlign: 'center',
});

const IconStyles = styled('div', {
  textAlign: 'center',
  marginTop: '20px',
});

export const MultiTinRegistrationV2 = ({ tinreg }) => {
  const { searchRegistrationStatusSaga } = useTinRegistrationStatus.state();
  const { registrationStatus, trackingNumber, reviewCompletedOn, submittedOn } =
    tinreg;

  return (
    <Loading isLoading={searchRegistrationStatusSaga.isLoading}>
      <div id="multi-tin-print-status">
        <Card>
          <Grid>
            <Grid.Col span="17%">
              <MarginStyle>
                <GrayText id="Status-ReferenceNumberText">
                  Reference number
                </GrayText>
                <TextStyleBold id="Status-ReferenceNumber">
                  {trackingNumber}
                </TextStyleBold>
              </MarginStyle>
              <MarginStyle>
                <GrayText id="Status-SubDateText">Submission date</GrayText>
                <TextStyleBold id="Status-SubDate">{submittedOn}</TextStyleBold>
              </MarginStyle>
            </Grid.Col>
            <Grid.Col span="2%">
              <Divider orientation="vertical" />
            </Grid.Col>
            <Grid.Col span="80%" style={{ paddingTop: '1em' }}>
              <Grid>
                <Grid.Col span="25%">
                  <IconStyles>
                    <IconCheckedCircle size="40" id="Status-subChecked" />
                  </IconStyles>
                </Grid.Col>
                <Grid.Col span="25%">
                  {registrationStatus !== 'PENDING' ? (
                    <IconStyles>
                      <IconCheckedCircle size="40" id="Status-reviewChecked" />
                    </IconStyles>
                  ) : (
                    <IconStyles>
                      <IconReview size="40" id="Status-review" />
                    </IconStyles>
                  )}
                </Grid.Col>
                {(registrationStatus === 'ACCEPTED' ||
                  registrationStatus === 'RELEASED') && (
                  <Grid.Col span="25%">
                    <IconStyles>
                      <IconAlignment>
                        <IconInventory size="34" id="Status-ClaimSubmitted" />
                      </IconAlignment>
                    </IconStyles>
                  </Grid.Col>
                )}
                <Grid.Col span="25%">
                  {registrationStatus === 'ACCEPTED' ? (
                    <IconStyles>
                      <IconLaptop size="40" id="Status-accessEligible" />
                    </IconStyles>
                  ) : registrationStatus === 'DENIED' ? (
                    <IconStyles>
                      <IconSymbol
                        icon="lock"
                        size={44}
                        color="gray"
                        variant="outlined"
                        id="Status-accessLockedNotEligible"
                      />
                    </IconStyles>
                  ) : (
                    <IconStyles>
                      <IconSymbol
                        icon="lock"
                        size={44}
                        variant="outlined"
                        id="Status-accessLocked"
                      />
                    </IconStyles>
                  )}
                </Grid.Col>
              </Grid>
              <Grid>
                <Grid.Col span="25%" style={{ paddingTop: '1em' }}>
                  <FlexStages>
                    <TextStyleBold id="Status-SubStageLabel">
                      TIN registration submission
                    </TextStyleBold>
                    <CompleteStatusText id="Status-SubStageStatus">
                      Complete
                    </CompleteStatusText>
                  </FlexStages>
                </Grid.Col>
                <Grid.Col span="25%" style={{ paddingTop: '1em' }}>
                  {registrationStatus !== 'PENDING' ? (
                    <FlexStages>
                      <TextStyleBold id="Status-ReviewStageLabel">
                        Review
                      </TextStyleBold>
                      <CompleteStatusText id="Status-ReviewStageStatus">
                        Complete
                      </CompleteStatusText>
                      <GrayText id="Status-ReviewStageCompletedOn">
                        Completed on {reviewCompletedOn}
                      </GrayText>
                    </FlexStages>
                  ) : (
                    <FlexStages>
                      <TextStyleBold id="Status-ReviewStageLabel">
                        Review
                      </TextStyleBold>
                      <InProgressStatusText id="Status-ReviewStageStatus">
                        In progress
                      </InProgressStatusText>
                      <GrayText id="Status-PortalReviewStatus">
                        Correspondence will be sent to you when the review is
                        complete
                      </GrayText>
                    </FlexStages>
                  )}
                </Grid.Col>
                {registrationStatus === 'ACCEPTED' && (
                  <Grid.Col span="25%" style={{ paddingTop: '1em' }}>
                    <FlexStages>
                      <TextStyleBold id="Status-PortalStageLabel">
                        Claim submitted
                      </TextStyleBold>
                      <CompleteStatusText id="Status-PortalStageStatus">
                        Complete
                      </CompleteStatusText>
                      <GrayText id="Status-PortalClaimStatus">
                        Provider portal is available
                      </GrayText>
                    </FlexStages>
                  </Grid.Col>
                )}
                {registrationStatus === 'RELEASED' && (
                  <Grid.Col span="25%" style={{ paddingTop: '1em' }}>
                    <FlexStages>
                      <TextStyleBold id="Status-PortalStageSubmitClaim">
                        Submit claim to UHC
                      </TextStyleBold>
                      <CompleteStatusText id="Status-PortalStatusAvailable">
                        Available
                      </CompleteStatusText>
                      <GrayText id="Status-PortalSubmitClaimStatus">
                        Submit claim for portal access
                      </GrayText>
                    </FlexStages>
                  </Grid.Col>
                )}
                <Grid.Col span="25%" style={{ paddingTop: '1em' }}>
                  {registrationStatus === 'ACCEPTED' ? (
                    <FlexStages>
                      <TextStyleBold id="Status-PortalStageLabel">
                        UnitedHealthcare <br /> Provider Portal access
                      </TextStyleBold>
                      <CompleteStatusText id="Status-PortalStageStatus">
                        Available now
                      </CompleteStatusText>
                      <Link
                        id="Status-PortalUrl"
                        href={config('PORTAL_REGISTRATION_URL')}
                      >
                        Register Now
                      </Link>
                    </FlexStages>
                  ) : registrationStatus === 'DENIED' ? (
                    <FlexStages>
                      <TextStyleBold id="Status-PortalStageLabel">
                        UnitedHealthcare <br /> Provider Portal access
                      </TextStyleBold>
                      <NotEligibleStatusText id="Status-PortalStageStatus">
                        Not eligible
                      </NotEligibleStatusText>
                    </FlexStages>
                  ) : (
                    <FlexStages>
                      <TextStyleBold id="Status-PortalStageLabel">
                        UnitedHealthcare <br /> Provider Portal access
                      </TextStyleBold>
                      <GrayText id="Status-PortalStageStatus">
                        Not available yet
                      </GrayText>
                    </FlexStages>
                  )}
                </Grid.Col>
              </Grid>
            </Grid.Col>
          </Grid>
        </Card>
      </div>
    </Loading>
  );
};

MultiTinRegistrationV2.propTypes = {
  tinreg: PropTypes.shape([]).isRequired,
};
